/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';

import { delModel, extrudeSettings, textureAnisotropy } from '../../../../../../../utils/Functions';
import { const_var, params } from '../../../../../../../redux/reducers/reducer';
import { CSG } from '../../../../../../../assets/libs/csg-lib';
import { MaterialInnerWall } from '../../../../../CustomMaterial';


const InsideWallModel = () => {
    const { gl } = useThree();

    const wallModel = useMemo(() => {
        let model = new THREE.Shape();

        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.p_h - ((params.p_r_p / 12) * params.p_w) : params.p_h
        const rightHeight = (params.singleSlope && params.freeStandingSide === "right") ? params.p_h + ((params.p_r_p / 12) * params.p_w) : params.p_h

        if (params.p_r_s === 1) {
            const innerRoofWidth = params.p_w - 0.1
            
            model.moveTo(-params.p_w / 2 + 0.01, params.p_h);
            model.lineTo(-params.p_w / 2 + 0.01, params.p_h + const_var.roofRegularDst);
            model.quadraticCurveTo( -params.p_w / 2 + 0.01, params.p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10,
                                    -params.p_w / 2 + 0.01 + const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12);
            model.lineTo(0, params.p_h + ((params.p_r_p / 12) * (params.p_w/2)) + const_var.roofRegularDst * 2 - 0.03);
            model.lineTo(params.p_w / 2 - 0.01 - const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12);
            model.quadraticCurveTo( params.p_w / 2 - 0.01, params.p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.p_r_p / 12) / 10,
                                    params.p_w / 2 - 0.01, params.p_h + const_var.roofRegularDst);
            model.lineTo(params.p_w / 2 - 0.01, params.p_h);
            model.lineTo(innerRoofWidth / 2 +0.005, 0);
            model.lineTo(-innerRoofWidth / 2 +0.005, 0);
        } else if (params.p_r_s === 2 || params.p_r_s === 3) {
            model.moveTo(-(params.p_w / 2 - 0.05), leftHeight - 0.001);
            if(!params.singleSlope) model.lineTo(0, params.p_h + (params.p_r_p / 12) * params.p_w / 2);
            model.lineTo((params.p_w / 2 - 0.05), rightHeight - 0.01);
            model.lineTo((params.p_w / 2 - 0.05), 0.01);
            model.lineTo(-(params.p_w / 2 - 0.05), 0.01);
        }
        model.closePath()

        return model;
    
    }, [params.p_w, params.p_h, params.p_r_s, params.p_r_p, params.singleSlope && params.freeStandingSide === "left"])

    const CSGWallModelGeometry = useMemo(() => {    

        /************** cutting hole using CSG ***************/

        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wallModel, extrudeSettings(0.005)))
        const meshDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('backStorageFront', 'wall'), extrudeSettings(1)))
        const meshRightOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('right', 'wall'), extrudeSettings(1)))
        const meshLeftOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('left', 'wall'), extrudeSettings(1)))

        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(0, 0, -params.p_d / 2 + parseInt(params.p_u_t) - 0.01)
        meshWallModel_Temp.updateMatrixWorld();

        let meshDelModel_Temp = meshDelModel.clone();
        meshDelModel_Temp.position.set(0, 0, -params.p_d / 2 + parseInt(params.p_u_t) - 0.01 -0.5);
        meshDelModel_Temp.updateMatrixWorld();

        let meshRightOffsetWithDoorDelModel_Temp = meshRightOffsetWithDoorDelModel.clone();
        meshRightOffsetWithDoorDelModel_Temp.position.set(params.p_w / 2 + 0.75, 0, -params.endStoragePositionZ);
        meshRightOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshRightOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        let meshLeftOffsetWithDoorDelModel_Temp = meshLeftOffsetWithDoorDelModel.clone();
        meshLeftOffsetWithDoorDelModel_Temp.position.set(-params.p_w / 2 + 0.25, 0, -params.endStoragePositionZ);
        meshLeftOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshLeftOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp);
        const meshRightOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshRightOffsetWithDoorDelModel_Temp);
        const meshLeftOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshLeftOffsetWithDoorDelModel_Temp);

        const wallModel_CSG =  meshWallModel_CSG.subtract(meshDelModel_CSG);
        const rightWallDel_CSG = wallModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);
        const leftWallDel_CSG = rightWallDel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);
        const CSGWallModel = CSG.toMesh(
            leftWallDel_CSG,
            new THREE.Matrix4()
        )

        return CSGWallModel.geometry;

        /****************************************** *********/
    }, [wallModel, params.endStoragePositionZ, params.p_u_t, params.p_d, params.p_w, const_var.newDoorsArray])

     


    return CSGWallModelGeometry && (
        <group onPointerOver={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} >
            <mesh name="c_b_f_s_w_inside_wall" geometry={CSGWallModelGeometry} position={[0, 0, 0.16]} >
                <MaterialInnerWall visibleSection={"center"} />
            </mesh>
        </group>
    )
}

export default InsideWallModel